import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { auth } from '../FirebaseConfig';
import { addDoc, Timestamp, collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { db } from "../FirebaseConfig";
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { onAuthStateChanged } from 'firebase/auth';

// Simple toast
const MessageToast = ({ message, type, onClose }) => {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => { setVisible(false); onClose?.(); }, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  if (!visible) return null;
  const color = type === 'success' ? 'bg-green-500' : 'bg-red-500';
  const icon = type === 'success' ? '✅' : '❌';
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
      className={`fixed top-5 left-1/2 -translate-x-1/2 p-4 rounded-xl text-white font-bold shadow-lg ${color} z-50`}
    >
      {icon} {message}
    </motion.div>
  );
};

export default function Testimonial() {
  const canvasRef = useRef(null);
  const controls = useAnimation();

  // --- UI/feedback ---
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState('success');
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Auth & Profile ---
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState(null);

  // --- Testimonials kept separate ---
  const [hardcodedTestimonials] = useState([
    { name: "Anya Johnson", Message: "This service transformed our property search. The listings were accurate and the team was incredibly responsive. A truly professional and modern experience." },
    { name: "Mark O'Malley", Message: "I was skeptical at first, but the results speak for themselves. The platform is intuitive, and I found my dream home faster than I ever thought possible." },
    { name: "Sophia Chen", Message: "The marketplace is so transparent. I could compare prices and find the best option quickly." },
    { name: "David Rodriguez", Message: "Trustworthy and efficient. I appreciated the transparency and the expert guidance provided throughout the entire process. Highly recommended!" },
    { name: "Emily White", Message: "I loved how the site felt so personalized and clean. The process was smooth and stress-free!" },
  ]);
  const [firebaseTestimonials, setFirebaseTestimonials] = useState([]);
  const [displayTestimonials, setDisplayTestimonials] = useState([]);

  // ---------- useEffect 1: AUTH ----------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  // ---------- useEffect 2: PROFILE (requires auth) ----------
  useEffect(() => {
    const loadProfile = async () => {
      // If no user or anonymous user, treat as no profile
      if (!user || user.isAnonymous) {
        setProfileData(null);
        return;
      }
      try {
        const ref = doc(db, 'profiles', user.uid);
        const snap = await getDoc(ref);
        setProfileData(snap.exists() ? snap.data() : null);
      } catch (e) {
        console.error('Error fetching profile:', e);
        setProfileData(null);
      }
    };
    loadProfile();
  }, [user]);

  // ---------- useEffect 3: FETCH TESTIMONIALS (separate from hardcoded) ----------
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const q = query(collection(db, 'Testimonial'), where('Status', '==', true));
        const snapshot = await getDocs(q);
        const fetched = snapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            name: data.name ?? 'Anonymous',
            // Normalize field name in case it's "message" or "Message"
            Message: data.Message ?? data.message ?? '',
            createdAt: data.createdAt ?? null,
          };
        });
        setFirebaseTestimonials(fetched);
      } catch (e) {
        console.error('Error fetching testimonials:', e);
        setToastMsg('Failed to load testimonials.');
        setToastType('error');
      }
    };
    fetchTestimonials();
  }, []);

  // ---------- COMBINE + SHUFFLE (Fisher–Yates, stable per change) ----------
  useEffect(() => {
    const combined = [...hardcodedTestimonials, ...firebaseTestimonials];
    const arr = combined.map(t => ({ ...t, Message: t.Message ?? t.message ?? '' })); // normalize
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    setDisplayTestimonials(arr);
    // Trigger card animations whenever the list changes
    controls.start((i) => ({
      y: 0,
      opacity: 1,
      transition: { delay: i * 0.08, duration: 0.8, ease: 'easeOut' }
    }));
  }, [hardcodedTestimonials, firebaseTestimonials, controls]);

  // ---------- 3D Scene (unaltered core logic) ----------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0);

    // Wireframe icosahedron
    const geometry = new THREE.IcosahedronGeometry(12, 1);
    const material = new THREE.MeshBasicMaterial({ color: 0x86efac, wireframe: true, transparent: true, opacity: 0.2 });
    const icosahedron = new THREE.Mesh(geometry, material);
    scene.add(icosahedron);

    // Glowing points at vertices
    const pointsGeometry = new THREE.BufferGeometry();
    const positions = geometry.attributes.position.array;
    const verts = [];
    for (let i = 0; i < positions.length; i += 3) {
      verts.push(positions[i], positions[i + 1], positions[i + 2]);
    }
    pointsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    const pointsMaterial = new THREE.PointsMaterial({ color: 0x86efac, size: 0.5, sizeAttenuation: true, alphaTest: 0.5, transparent: true, opacity: 0.8 });
    const points = new THREE.Points(pointsGeometry, pointsMaterial);
    scene.add(points);

    camera.position.z = 20;

    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      icosahedron.rotation.x += 0.0005;
      icosahedron.rotation.y += 0.0008;
      points.rotation.x += 0.0005;
      points.rotation.y += 0.0008;
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      geometry.dispose();
      material.dispose();
      pointsGeometry.dispose();
      pointsMaterial.dispose();
      renderer.dispose();
    };
  }, []);

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Block anonymous or no-profile users
    if (!user || user.isAnonymous) {
      setToastMsg('Please sign in with a non-anonymous account to submit a review.');
      setToastType('error');
      setIsSubmitting(false);
      return;
    }
    if (!profileData) {
      setToastMsg('Please complete your profile before submitting a review.');
      setToastType('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const userName = profileData.name || auth.currentUser?.displayName || 'Anonymous';
      const userEmail = profileData.userEmail || auth.currentUser?.email || 'Anonymous';

      await addDoc(collection(db, 'Testimonial'), {
        name: userName,
        Message: feedbackMessage,          // keep "Message" (capital M) to match your collection
        Status: false,                     // requires admin approval
        email: userEmail,
        createdAt: Timestamp.now(),
      });

      setToastMsg('Review submitted! It will be visible after admin approval.');
      setToastType('success');
      setFeedbackMessage('');
      setShowFeedbackForm(false);
    } catch (err) {
      console.error('Error adding testimonial:', err);
      setToastMsg('Something went wrong. Please try again.');
      setToastType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-black text-white font-['Space_Grotesk']">
      {/* 3D Canvas (still there) */}
      <canvas ref={canvasRef} className="fixed inset-0 z-0 opacity-50 pointer-events-none" />

      {toastMsg && (
        <MessageToast message={toastMsg} type={toastType} onClose={() => setToastMsg('')} />
      )}

      <div className="relative z-10 p-6 sm:p-10 flex flex-col items-center">
        <div className="flex flex-col md:flex-row justify-between items-center w-full max-w-7xl mb-8 md:mb-12">
          <div className="text-center md:text-left">
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-2">
              Trusted by Our Clients
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl">
              Read what our clients have to say about their experience.
            </p>
          </div>
          <motion.button
            onClick={() => setShowFeedbackForm(true)}
            className="mt-4 md:mt-0 px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition-all shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Submit a Review
          </motion.button>
        </div>

        {/* Combined + Shuffled */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {displayTestimonials.map((t, i) => (
            <motion.div
              key={t.id ?? `hc-${i}`}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-white/20 flex flex-col"
              custom={i}
              initial={{ y: 200, opacity: 0 }}
              animate={controls}
            >
              <p className="flex-grow text-gray-200 leading-relaxed italic">
                "{t.Message}"
              </p>
              <p className="mt-4 font-bold text-lg text-emerald-400">
                - {t.name}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {showFeedbackForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: -50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-xl rounded-2xl bg-white/10 p-8 shadow-2xl border border-white/20"
            >
              <button
                onClick={() => setShowFeedbackForm(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>

              <h2 className="text-3xl font-bold mb-6 text-emerald-400">Share Your Experience</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  placeholder="Your Feedback"
                  rows="4"
                  required
                  className="w-full bg-transparent border border-emerald-500/50 text-white placeholder-gray-400 rounded-lg p-3 outline-none transition-colors focus:border-emerald-400"
                />
                <motion.button
                  type="submit"
                  disabled={isSubmitting || !feedbackMessage}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-500 text-white font-bold py-3 rounded-xl transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? "Submitting..." : "Submit feedback"}
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
